/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/usePricelistPicker.ts
 * FILE_ID: QB-AGGRID-PRICELIST-HOOK-001
 * PURPOSE: Stateful hook orchestrating the SAM-style pricelist picker:
 *          owns the modal open/close state, decides which row (focused
 *          real row vs topmost pad) the chosen item should land on,
 *          and dispatches the right persist callback.
 * WHY: ScopeAgGridSection should stay close to its 300-line cap; this
 *      hook isolates the pick-and-prefill state machine so the
 *      orchestrator just renders <PricelistPickerModal/> and forwards
 *      a single `open` callback to the toolbar.
 * FLOW:
 *   ┌─────────────┐   ┌────────────────┐   ┌────────────────────────┐
 *   │ toolbar     │ → │ open()         │ → │ modal renders +        │
 *   │ button      │   │ (isOpen=true)  │   │ user searches +        │
 *   └─────────────┘   └────────────────┘   │ picks PriceBookItem    │
 *                                          └────────────────────────┘
 *                                                       │
 *                              ┌────────────────────────┘
 *                              ▼
 *               ┌────────────────────────────────┐
 *               │ handleSelect(item):            │
 *               │  - focused row & not pad?      │
 *               │      → onUpdateItem(patch)     │
 *               │  - focused row is pad?         │
 *               │      → onAddItem(create)       │
 *               │  - no focus + has rows?        │
 *               │      → onUpdateItem on last    │
 *               │  - empty grid?                 │
 *               │      → onAddItem(create) → pad │
 *               └────────────────────────────────┘
 * RELATED:
 *   - pricelist-prefill.ts — pure payload builders
 *   - PricelistPickerModal.tsx — the modal UI
 *   - pad-row-helpers.ts — isPadRowId / PadRowSeed
 */

import { useCallback, useState } from 'react'
import type { PriceBookItem } from '@/lib/api/pricing-api/types'
import type { QuoteLineItem } from '@/lib/quoteApi'
import type { PadRowSeed } from './pad-row-helpers'
import { isPadRowId } from './pad-row-helpers'
import {
  buildPricelistCreatePayload,
  buildPricelistUpdatePatch,
} from './pricelist-prefill'

export interface UsePricelistPickerArgs {
  sectionId: string
  rows: QuoteLineItem[]
  padSeed: PadRowSeed
  isEditable: boolean
  /** ID of the currently-focused row (from the formula bar state). */
  focusedRowId: string | null
  onAddItem: (sectionId: string, data: Record<string, unknown>) => void
  onUpdateItem: (
    sectionId: string,
    itemId: string,
    patch: Partial<QuoteLineItem>,
  ) => void
  onPrefillTarget?: (target: PrefillTarget) => void
}

export interface UsePricelistPickerResult {
  /** True iff the modal is currently open. */
  isOpen: boolean
  /** Open the modal (toolbar button). No-op when not editable. */
  open: () => void
  /** Close the modal (Esc, backdrop click). */
  close: () => void
  /** Handle the user picking an item from the modal. */
  handleSelect: (item: PriceBookItem) => void
}

/**
 * Decide which row the prefill should land on. Pure function so the
 * routing is testable without the surrounding hook noise.
 *
 * Priority:
 *   1. Focused real row (most explicit user intent)
 *   2. Topmost pad row (SAM default — "add new row from pricelist")
 *   3. Last real row (estimator opened picker while nothing focused;
 *      fall back to "update the row I just typed")
 */
export type PrefillTarget =
  | { kind: 'update'; rowId: string }
  | { kind: 'create' }

export function resolvePrefillTarget(
  rows: QuoteLineItem[],
  focusedRowId: string | null,
): PrefillTarget {
  if (focusedRowId && !isPadRowId(focusedRowId)) {
    const exists = rows.some((r) => r.id === focusedRowId)
    if (exists) return { kind: 'update', rowId: focusedRowId }
  }
  // No usable focus → create a fresh row via the pad-promote path.
  // (We never write directly to a pad id; onAddItem owns the create.)
  return { kind: 'create' }
}

export function usePricelistPicker(
  args: UsePricelistPickerArgs,
): UsePricelistPickerResult {
  const {
    sectionId,
    rows,
    padSeed,
    isEditable,
    focusedRowId,
    onAddItem,
    onUpdateItem,
    onPrefillTarget,
  } = args
  const [isOpen, setIsOpen] = useState(false)

  const open = useCallback(() => {
    if (!isEditable) return
    setIsOpen(true)
  }, [isEditable])

  const close = useCallback(() => setIsOpen(false), [])

  const handleSelect = useCallback(
    (item: PriceBookItem) => {
      if (!isEditable) {
        setIsOpen(false)
        return
      }
      const target = resolvePrefillTarget(rows, focusedRowId)
      if (target.kind === 'update') {
        onUpdateItem(sectionId, target.rowId, buildPricelistUpdatePatch(item))
      } else {
        onAddItem(sectionId, buildPricelistCreatePayload(item, padSeed))
      }
      onPrefillTarget?.(target)
      setIsOpen(false)
    },
    [
      isEditable,
      rows,
      focusedRowId,
      sectionId,
      padSeed,
      onAddItem,
      onUpdateItem,
      onPrefillTarget,
    ],
  )

  return { isOpen, open, close, handleSelect }
}
